// src/pages/Dashboard/Payouts/WireTransferOnboarding.tsx
// ⭐ Premium 3D version — Areluxe Gold UI

import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { X } from "lucide-react";
import { TextField, Paper } from "@mui/material";
import { upsertPayoutMethod } from "../../redux/payoutMethodSlice";

const gold = "#0A66C2";

const WireTransferOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const params = useParams();
  const routeParamUserId = params.userId;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authenticatedId = authUser?.userId;

  const userId = routeParamUserId || authenticatedId;

  useEffect(() => {
    if (!userId) navigate("/AuthOverview");
  }, [userId]);

  // FORM STATE
  const [beneficiaryFirst, setBeneficiaryFirst] = useState("");
  const [beneficiaryLast, setBeneficiaryLast] = useState("");

  const [iban, setIban] = useState("");
  const [swift, setSwift] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAddress, setBankAddress] = useState("");

  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");

  // VALIDATION
  const swiftValid = swift.trim().length >= 8 && swift.trim().length <= 11;
  const namesValid =
    beneficiaryFirst.trim() !== "" && beneficiaryLast.trim() !== "";
  const accountMatch = accountNumber === confirmAccountNumber;
  const accountValid = accountNumber.trim().length >= 6;
  const bankInfoValid = bankName.trim().length >= 2;

  const isValid = useMemo(() => {
    return (
      namesValid &&
      swiftValid &&
      accountValid &&
      accountMatch &&
      bankInfoValid
    );
  }, [namesValid, swiftValid, accountValid, accountMatch, bankInfoValid]);

  // SUBMIT
  const handleNext = async () => {
    if (!isValid || !userId) return;

    const payload = {
      payoutMethod: "wire",

      // Required by BasePayoutMethod
      fullName: `${beneficiaryFirst} ${beneficiaryLast}`,
      legalType: "PRIVATE",
      currency: "USD",

      // Wire-specific
      beneficiaryFirst,
      beneficiaryLast,
      iban,
      swift,
      bankName,
      bankAddress,
      accountNumber,
    };

    await dispatch(
      upsertPayoutMethod({
        userId,
        payoutMethodKey: "wire",
        payoutMethod: payload as any,
      })
    );

    navigate(`/PaymentMethodDashboard/${userId}`);
  };

  const tf = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      background: "rgba(255, 255, 255, 1)",
      border: "1px solid rgba(48, 174, 191, 0.35)",
    },
    "& .MuiInputLabel-root": { color: "#737373ff" },
    "& .MuiOutlinedInput-input": { color: "#000000ff" },
  };

  return (
    <div
      style={{
        background: "#0A66C2",
        minHeight: "100vh",
        paddingTop: 30,
        display: "flex",
        flexDirection: "column",
        color: "#fff",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          padding: 16,
          display: "flex",
          alignItems: "center",
          maxWidth: 500,
          width: "100%",
          margin: "0 auto",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: 10,
            padding: "6px 12px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(48, 191, 191, 0.3)",
            cursor: "pointer",
          }}
        >
          <X size={20} color="#fff" />
          <span style={{ marginLeft: 6, color: "#fff", fontWeight: 600 }}>
            Exit
          </span>
        </button>
      </div>

      {/* CONTENT */}
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 40 }}>
        <Paper
          elevation={10}
          style={{
            maxWidth: 500,
            width: "100%",
            margin: "0 auto",
            padding: "22px 20px",
            background: "linear-gradient(145deg,#0c0c0c,#87CEEB)",
            borderRadius: 22,
            border: "1px solid rgba(191,155,48,0.25)",
            boxShadow:
              "0 8px 25px rgba(0,0,0,0.5), inset 0 0 12px rgba(191,155,48,0.15)",
          }}
        >
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              marginBottom: 10,
              color: gold,
            }}
          >
            Wire Transfer Details
          </h2>

          {/* BENEFICIARY NAME */}
          <h3 style={styles.sectionTitle}>Beneficiary Details</h3>

          <TextField
            fullWidth
            label="First name"
            value={beneficiaryFirst}
            placeholder="e.g. John"
            onChange={(e) => setBeneficiaryFirst(e.target.value)}
            sx={tf}
          />

          <TextField
            fullWidth
            label="Last name"
            placeholder="e.g. Smith"
            value={beneficiaryLast}
            onChange={(e) => setBeneficiaryLast(e.target.value)}
            sx={tf}
          />

          {/* IBAN */}
          <TextField
            fullWidth
            label="IBAN (optional)"
            placeholder="e.g. EE121010010123456789"
            value={iban}
            onChange={(e) => setIban(e.target.value.toUpperCase())}
            sx={tf}
          />

          {/* BANK DETAILS */}
          <h3 style={styles.sectionTitle}>Bank Details</h3>

          <TextField
            fullWidth
            label="SWIFT / BIC"
            placeholder="e.g. CHASUS33 or DEUTDEFF"
            value={swift}
            onChange={(e) => {
              const cleaned = e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "");
              if (cleaned.length <= 11) setSwift(cleaned);
            }}
            error={swift.length > 0 && !swiftValid}
            helperText={
              swift.length === 0
                ? "Enter 8–11 character SWIFT/BIC code"
                : swiftValid
                ? "SWIFT looks valid ✓"
                : "SWIFT must be 8–11 characters"
            }
            sx={tf}
          />

          <TextField
            fullWidth
            label="Bank Name"
            placeholder="e.g. Bank of America"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            sx={tf}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Bank Address"
            placeholder="e.g. 123 Main Street, New York, NY 10001"
            value={bankAddress}
            onChange={(e) => setBankAddress(e.target.value)}
            sx={tf}
          />

          {/* ACCOUNT NUMBERS */}
          <h3 style={styles.sectionTitle}>Account Number</h3>

          <TextField
            fullWidth
            label="Account number"
            placeholder="e.g. 000123456789"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            sx={tf}
          />

          <TextField
            fullWidth
            label="Confirm account number"
            placeholder="Re-enter account number"
            value={confirmAccountNumber}
            onChange={(e) => setConfirmAccountNumber(e.target.value)}
            error={confirmAccountNumber.length > 0 && !accountMatch}
            helperText={
              confirmAccountNumber.length > 0
                ? accountMatch
                  ? "Account numbers match ✓"
                  : "Account numbers do not match"
                : ""
            }
            sx={tf}
          />
        </Paper>
      </div>

      {/* FOOTER */}
      <div
        style={{
          padding: "20px",
          paddingBottom: 30,
          maxWidth: 500,
          width: "100%",
          margin: "0 auto",
        }}
      >
        <button
          onClick={handleNext}
          disabled={!isValid}
          style={{
            background:
              "linear-gradient(90deg,#1a1a1a,#000000,#1a1a1a,#000000)",
            padding: "14px 0",
            borderRadius: 14,
            width: "100%",
            color: gold,
            fontSize: 17,
            fontWeight: 800,
            opacity: isValid ? 1 : 0.5,
            cursor: isValid ? "pointer" : "not-allowed",
            border: "1px solid rgba(191,155,48,0.4)",
            boxShadow: "0 5px 15px rgba(191,155,48,0.25)",
            marginBottom: 20,
          }}
        >
          Save
        </button>

        <button
          onClick={() => navigate(-1)}
          style={{
            width: "100%",
            background: "none",
            border: "none",
            padding: "8px 0",
            color: gold,
            fontWeight: 600,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default WireTransferOnboarding;

const styles: Record<string, React.CSSProperties> = {
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginTop: 25,
    marginBottom: 10,
    color: gold,
  },
};
